import type { SerializedError } from "@reduxjs/toolkit"
import { type FetchBaseQueryError, skipToken } from "@reduxjs/toolkit/query"

import { useAuthStatusGetQuery, useUserGetMeQuery } from "@/api/routes/users"
import { type User, type UserOrTeamRes, UserType } from "@/types"
import { UserLocalStorage } from "@/user-session-storage"
import { useParams } from "react-router"

type CurrentUserHook = {
  username?: string
  isLoggedIn?: boolean
  data?: UserOrTeamRes
  isLoading: boolean
  isSuccess: boolean
  isError: boolean
  error?: FetchBaseQueryError | SerializedError
  me?: User
  refetch: () => void
}

// This hook can be used anywhere in the app, and returns a meta-request that is
// the result of querying /auth_status, and then /me & /my-projects if we are in
// fact logged in.
// The response looks like a normal API query, but has two extra fields:
// * isLoggedIn - will be undefined while the /auth_status request
//                is loading, true if the user is logged in,
//                and false in all other cases
// * username   - the username loaded from a URL param if present
// * me         - the current logged-in user, if present
// keep in mind the "data" param will be the *selected* user
export function useSessionUser(): CurrentUserHook {
  const { username } = useParams()
  const authStatusReq = useAuthStatusGetQuery()
  const isLoggedIn = authStatusReq.data?.authenticated
  const userReq = useUserGetMeQuery(isLoggedIn ? undefined : skipToken)

  const refetch = () => {
    !authStatusReq.isUninitialized && authStatusReq.refetch()
    !userReq.isUninitialized && userReq.refetch()
  }

  if (isLoggedIn === false) {
    return {
      username,
      isLoggedIn: false,
      isLoading: false,
      isSuccess: true,
      isError: false,
      refetch,
    }
  }

  if (authStatusReq.isLoading) {
    return {
      username,
      isLoading: true,
      isSuccess: false,
      isError: false,
      refetch,
    }
  }

  if (authStatusReq.isLoading || userReq.isLoading) {
    return loadingRes(username, refetch)
  }
  if (authStatusReq.isError) {
    return errRes(username, authStatusReq.error, refetch)
  }
  if (userReq.isError) {
    return errRes(username, userReq.error, refetch)
  }

  if (!userReq.data) {
    return {
      username,
      isLoading: false,
      isSuccess: true,
      isError: false,
      refetch,
    }
  }

  UserLocalStorage.setUserId(userReq.data.id)
  const user = {
    user_type: UserType.individual,
    id: userReq.data.id,
    username: userReq.data.username,
  }
  return res(username, user, refetch, userReq.data)
}

function res(
  username: string | undefined,
  data: UserOrTeamRes,
  refetch: () => void,
  me: User | undefined,
): CurrentUserHook {
  return {
    data,
    username,
    me,
    isLoading: false,
    isSuccess: true,
    isError: false,
    isLoggedIn: true,
    refetch,
  }
}

function errRes(
  username: string | undefined,
  error: FetchBaseQueryError | SerializedError,
  refetch: () => void,
) {
  return {
    username,
    isLoggedIn: true,
    data: undefined,
    isLoading: false,
    isSuccess: false,
    isError: true,
    error,
    refetch,
  } satisfies CurrentUserHook
}

function loadingRes(username: string | undefined, refetch: () => void) {
  return {
    username,
    isLoggedIn: true,
    data: undefined,
    isLoading: true,
    isSuccess: false,
    isError: false,
    refetch,
  } satisfies CurrentUserHook
}
